import { AppLayout } from "@/components/layout/app-layout";
import { PageHeader } from "@/components/layout/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { TrendingUp, TrendingDown, AlertTriangle, Target, Brain, BarChart3 } from "lucide-react";

const StrategicIntel = () => {
  const insights = [
    {
      id: 1,
      title: "Market Volatility Alert",
      description: "Increased volatility detected in tech sector. Consider hedging strategies.",
      severity: "high",
      category: "Market Risk",
      timestamp: "2 hours ago",
      impact: "High"
    },
    {
      id: 2,
      title: "Emerging Opportunity",
      description: "Green energy ETFs showing strong momentum with regulatory tailwinds.",
      severity: "medium",
      category: "Opportunity",
      timestamp: "4 hours ago",
      impact: "Medium"
    },
    {
      id: 3,
      title: "Client Concentration Risk",
      description: "Portfolio concentration in single client exceeds 15% threshold.",
      severity: "high",
      category: "Risk Management",
      timestamp: "1 day ago",
      impact: "High"
    }
  ];

  const marketTrends = [
    { sector: "Technology", trend: "up", change: "+2.4%", confidence: "High" },
    { sector: "Healthcare", trend: "up", change: "+1.8%", confidence: "Medium" },
    { sector: "Energy", trend: "down", change: "-1.2%", confidence: "High" },
    { sector: "Financial", trend: "up", change: "+0.9%", confidence: "Low" }
  ];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "high": return "border-red-500 bg-red-500/10";
      case "medium": return "border-yellow-500 bg-yellow-500/10";
      case "low": return "border-green-500 bg-green-500/10";
      default: return "border-gray-500 bg-gray-500/10";
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case "high": return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case "medium": return <Target className="h-4 w-4 text-yellow-500" />;
      default: return <Brain className="h-4 w-4 text-blue-500" />;
    }
  };

  return (
    <AppLayout>
      <PageHeader 
        title="Strategic Intelligence"
        subtitle="AI-powered insights and market intelligence for strategic decision making"
      />
      
      <main className="flex-1 p-page-padding lg:p-page-padding-lg space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Strategic Intelligence</h1>
              <p className="text-muted-foreground mt-1">
                AI-powered insights and market intelligence for strategic decision making
              </p>
            </div>
            <Button>Generate Report</Button>
          </div>

          {/* Intelligence Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Brain className="h-4 w-4" />
                  Active Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">23</div>
                <p className="text-xs text-muted-foreground">+5 new today</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  High Priority
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">7</div>
                <p className="text-xs text-muted-foreground">Require attention</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Market Score
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">78/100</div>
                <p className="text-xs text-muted-foreground">Favorable conditions</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Target className="h-4 w-4" />
                  Accuracy Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">94.2%</div>
                <p className="text-xs text-muted-foreground">Last 30 days</p>
              </CardContent>
            </Card>
          </div>

          {/* Active Insights */}
          <Card>
            <CardHeader>
              <CardTitle>Active Strategic Insights</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {insights.map((insight) => (
                  <Alert key={insight.id} className={getSeverityColor(insight.severity)}>
                    <div className="flex items-start gap-3">
                      {getSeverityIcon(insight.severity)}
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold">{insight.title}</h4>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary">{insight.category}</Badge>
                            <Badge variant="outline">{insight.impact} Impact</Badge>
                          </div>
                        </div>
                        <AlertDescription className="text-sm">
                          {insight.description}
                        </AlertDescription>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-muted-foreground">{insight.timestamp}</span>
                          <Button variant="ghost" size="sm">View Details</Button>
                        </div>
                      </div>
                    </div>
                  </Alert>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Market Intelligence */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Sector Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {marketTrends.map((sector, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          {sector.trend === "up" ? (
                            <TrendingUp className="h-4 w-4 text-green-500" />
                          ) : (
                            <TrendingDown className="h-4 w-4 text-red-500" />
                          )}
                          <span className="font-medium">{sector.sector}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`font-semibold ${
                          sector.trend === "up" ? "text-green-600" : "text-red-600"
                        }`}>
                          {sector.change}
                        </span>
                        <Badge variant={
                          sector.confidence === "High" ? "default" : 
                          sector.confidence === "Medium" ? "secondary" : "outline"
                        }>
                          {sector.confidence}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>AI Recommendations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 border rounded-lg bg-green-500/10 border-green-500/20">
                    <h4 className="font-semibold text-green-700 mb-2">Rebalancing Opportunity</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      Consider reducing tech exposure by 5% and increasing defensive positions.
                    </p>
                    <Button size="sm" variant="outline">Execute Strategy</Button>
                  </div>
                  
                  <div className="p-4 border rounded-lg bg-blue-500/10 border-blue-500/20">
                    <h4 className="font-semibold text-blue-700 mb-2">ESG Allocation</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      Increasing client demand for ESG investments detected. Consider new allocations.
                    </p>
                    <Button size="sm" variant="outline">Review Options</Button>
                  </div>
                  
                  <div className="p-4 border rounded-lg bg-yellow-500/10 border-yellow-500/20">
                    <h4 className="font-semibold text-yellow-700 mb-2">Currency Hedge</h4>
                    <p className="text-sm text-muted-foreground mb-2">
                      USD strength may impact international positions. Consider hedging strategies.
                    </p>
                    <Button size="sm" variant="outline">Analyze Impact</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
      </main>
    </AppLayout>
  );
};

export default StrategicIntel;